import React, { ReactNode, ButtonHTMLAttributes, useEffect, useState } from "react";

interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading: boolean;
  loadingText?: string;
  delayedLoadingText?: string;
  loadingTextDelayMs?: number;
  children: ReactNode;
}

const LoadingButton = ({
  loading,
  loadingText = "Loading",
  delayedLoadingText,
  loadingTextDelayMs = 3000,
  children,
  disabled,
  ...rest
}: LoadingButtonProps) => {
  const [showDelayedLoadingText, setShowDelayedLoadingText] = useState(false);

  useEffect(() => {
    if (!loading || !delayedLoadingText) {
      setShowDelayedLoadingText(false);
      return;
    }

    const timeoutId = setTimeout(() => setShowDelayedLoadingText(true), loadingTextDelayMs);
    return () => clearTimeout(timeoutId);
  }, [delayedLoadingText, loading, loadingTextDelayMs]);

  return (
    <button
      className={`block w-full text-[15px] font-bold tracking-wider py-3 px-[35px] bg-black text-white text-center cursor-pointer h-auto justify-center items-center box-border rounded-full appearance-none duration-200 transition-transform mt-5 sm:leading-7 disabled:bg-gray-300 ${
        loading
          ? "scale-100 hover:scale-100 disabled:!bg-[linear-gradient(110deg,_#1f2937_25%,_#374151_50%,_#1f2937_75%)] bg-[length:200%_100%] motion-safe:animate-queue-button-wipe"
          : "hover:scale-105"
      } min-h-[48px]`}
      disabled={loading || disabled}
      {...rest}
    >
      {loading && (
        <div className="flex justify-center items-center" aria-live="polite">
          <span className="motion-safe:animate-queue-loading-text">
            {showDelayedLoadingText ? delayedLoadingText : loadingText}
          </span>
        </div>
      )}
      {!loading && children}
    </button>
  );
};

export default LoadingButton;
